import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl';
import type { AppPropsState } from '@atlassian/jira-servicedesk-common/src/state/app-props/types';

export type AppLinkId = string;
export const toAppLinkId: (arg1: string) => AppLinkId = (appLinkId: string) => appLinkId;

/**
 * Categories are groupings of knowledge. Knowledge can be articles, etc.
 *
 * CategoryGuid would be the generated id by the jsd-apollo service.
 * The string literals ("all-articles", etc) are hard coded groupings of knowledge.
 * For example "all-articles" would container all the articles in the a projects knowledge base.
 */
export type CategoryId = CategoryGuid | 'all-articles';
export type CategoryGuid = string;
export const categoryGuidToString = (guid: CategoryGuid): string => guid;
export const toCategoryGuid = (guid: string): CategoryGuid => guid;

export type AppProps = AppPropsState;

export type Category = {
	id: CategoryGuid;
	name: string;
	description?: string;
	categoryUrl: string;
};

export type Label = {
	id: string;
	label: string;
	name: string;
};

export type LabelState = {
	labels: Label[];
	addLabelError: boolean;
	isLoading: boolean;
};

export type Categories = Category[];

export type CategoryField = 'name' | 'description';

export type ArticleType = 'article';

export type KnowledgeUser = {
	accountId: string;
	displayName: string;
	avatarUrl: string;
};

/**
 * The article shape that is reduced into our redux store.
 * Don't mistake this for the article that is returned from
 * Apollo API.
 */
export type ArticlePersistedState = {
	type: ArticleType | undefined;
	id: string;
	title: string;
	viewUrl: string;
	editUrl: string;
	shareUrl: string;
	lastModified: string;
	friendlyLastModified: string;
	categories: CategoryGuid[] | undefined;
	categoriesSupported?: boolean;
	featuredInCategories: CategoryGuid[] | undefined;
	viewCount?: number;
	usedCount: number;
	createdBy?: KnowledgeUser;
	titleExcerpt?: string;
	summaryExcerpt?: string;
	appLinkId?: AppLinkId;
	isBusy?: boolean;
	spaceName?: string;
	spaceUrl?: string;
	spaceId: number | undefined;
	spaceKey?: string;
	isEditable?: boolean;
	labelState?: LabelState;
};

/**
 * The article shape that is returned by the article selectors.
 * This is essentially the same as the ArticlePersistedState except the categories
 * details are also included and invalid categories are filtered out
 */
export type Article = Omit<ArticlePersistedState, 'categories'> & {
	categories: Categories | undefined;
	categoriesSupported?: boolean;
};

export type Knowledge = Article;

export type AppLink = {
	isSystem: boolean;
	isPrimary: boolean;
	isServer: boolean;
	appLinkId: AppLinkId;
	name: string;
	displayUrl: string;
	authUrl: string;
	requireCredentials: boolean;
};

export type ConfluenceLink = {
	spaceKey: string;
	baseUrl: string;
	id: string;
	projectKey: string;
};

export type ReactIntlMessage = MessageDescriptor;

export type ConfluenceLinkError = {
	error: {
		message: string;
		key: string;
	};
};

export type GenericErrorObject = {
	header: ReactIntlMessage;
	message: ReactIntlMessage;
	ctaText?: ReactIntlMessage;
	ctaLink?: string;
};

export type GenericError = {
	error: GenericErrorObject;
};

export type FormError = {
	message: ReactIntlMessage;
};

export type FieldErrors = {
	[field: string]: FormError;
};

export type JiraError = {
	errorMessage: string;
};

export type JiraErrors = {
	errors: JiraError[];
	reasonKey: string;
	reasonCode: string;
};

export type ConfluenceLinkClaim = ConfluenceLink | ConfluenceLinkError | undefined;

type LoadingLinkState = {
	linkedState: 'loading';
};

type UnlinkedLinkState = {
	linkedState: 'unlinked';
};

type LinkedLinkState = {
	linkedState: 'linked';
	spaceKey: string;
	confluenceUrl: string;
	id: string;
};

type ErrorLinkState = {
	linkedState: 'error';
	messageKey: string;
};

export type ConfluenceLinkState =
	| LoadingLinkState
	| UnlinkedLinkState
	| LinkedLinkState
	| ErrorLinkState;

export type ServiceError = {
	statusCode: number;
	errorTitle: string;
	errorMessage: string;
};

export type KbLink = {
	appLinkId: AppLinkId;
	appLinkName: string;
	appLinkUrl: string;
	spaceKey: string;
	spaceName: string;
	spaceUrl: string;
};

export type LabelSuggestions = {
	label: string;
	value: string;
};

type ContentNameMatch = {
	name: string;
	className: string;
}[];

export type LabelSuggestionsResponse = {
	contentNameMatches?: ContentNameMatch[];
};

export type UpdateLabelRequest = {
	name: string;
};
